import mitt from 'mitt';

type Events<V> = {
  onDelete: V;
  onClear: void;
};

export class LRUMap<K, V> {
  private _defaultMaxLimit: number;
  private _maxLimit: number;
  private _cache: Map<K, V>;
  public emitter;

  constructor(maxLimit: number) {
    this._defaultMaxLimit = maxLimit;
    this._maxLimit = maxLimit;
    this._cache = new Map();

    this.emitter = mitt<Events<V>>();
  }

  public increaseMaxLimit(gap: number) {
    this._maxLimit = this._maxLimit + gap;
  }

  public resetMaxLimit() {
    this._maxLimit = this._defaultMaxLimit;
  }

  public resetDefaultMaxLimit(maxLimit: number) {
    this._defaultMaxLimit = maxLimit;
    this._maxLimit = maxLimit;
  }

  public get(key: K) {
    const value = this._cache.get(key);
    if (value) {
      this._cache.delete(key);
      this._cache.set(key, value);
    }
    return value;
  }

  public set(key: K, value: V) {
    if (this._cache.has(key)) {
      this._cache.delete(key);
    }

    this._cache.set(key, value);

    if (this._cache.size > this._maxLimit) {
      const delKey = this._cache.keys().next().value;
      const delValue = this._cache.get(delKey);
      if (delValue) {
        this.emitter.emit('onDelete', delValue);
      }
      this._cache.delete(this._cache.keys().next().value);
    }
  }

  public delete(k: K) {
    const delValue = this._cache.get(k);
    const result = this._cache.delete(k);
    if (result && delValue) {
      this.emitter.emit('onDelete', delValue);
    }
    return result;
  }

  public has(key: K) {
    return this._cache.has(key);
  }

  public isEmpty() {
    return this._cache.size === 0;
  }

  public clear() {
    this.resetMaxLimit();
    this._cache.clear();
    this.emitter.emit('onClear');
  }

  public values() {
    return Array.from(this._cache.values());
  }

  public keys() {
    return Array.from(this._cache.keys());
  }
}
